import React, { useRef, useState } from 'react';
import Tool, { type ToolButtonComponent } from './Tool';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolButton from '../toolbars/ToolButton';
import WhiteboardController from '../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import styles from '../styles.module.css';
import ToolButtonWithLabel from '../toolbars/ToolButtonWithLabel';

const SIZE_SCALE_VALUES = {
  Original: 1,
  '50%': 0.5,
  '25%': 0.25,
} as const;

const EM_DASH = '\u2014';

interface PopupProps {
  setCurrentSize: (size: string) => void;
  selection: any;
  hidePopup: () => void;
}

const Popup: React.FC<PopupProps> = ApplicationView(
  ({ setCurrentSize, selection, hidePopup }) => {
    const controller = useController(WhiteboardController);

    const setSize = size => {
      setCurrentSize(size);

      const image = selection.firstObject();

      // Move the image so it's center stays in the same position
      const widthChange = size * image.width - image.scaleX * image.width;
      let newLeft = image.left - widthChange / 2;

      let newTop: number = image.top;

      const canvasWrapper = controller.canvas.containerElement;
      const toolbar = canvasWrapper.querySelector(
        "[data-testid='Object-toolbar']"
      );

      if (toolbar && canvasWrapper) {
        // If we can find the object toolbar, then we want the image to be in
        // a good position relative to that bar after the resize. There are many
        // different conditions this can hit.

        const toolbarRect = toolbar.getBoundingClientRect();
        const wrapperRect = canvasWrapper.getBoundingClientRect();

        // Base everything off the center of the toolbar
        const relativeX =
          toolbarRect.left + toolbarRect.width / 2 - wrapperRect.left;
        const relativeY =
          toolbarRect.top + toolbarRect.height / 2 - wrapperRect.top;

        const toolbarMiddleCanvasCoords = controller.canvas.pointToCanvasCoords(
          { x: relativeX, y: relativeY }
        );

        // Horizontal considerations: If the toolbar is not aligned with the
        // middle of the image because the image is off the screen to the left
        // or right, then we want to pin the image to its left or right side.
        // We only do this if it won't cause the image to end up completely over
        // the midpoint of the toolbar. If that were to happend, then we just
        // center the image on the toolbar.

        // This will be the position so the image's center stays horizontally
        // fixed. We use this as a fallback below if using the left or right
        // edge as the anchor would move the image too far out of alignment with
        // the toolbar
        const newLeftToAlignWithToolbarMiddle =
          toolbarMiddleCanvasCoords.x - (image.width * size) / 2;
        const originalImageMiddle =
          image.left + (image.width * image.scaleX) / 2;
        // Allow for the middle to be slightly off to account for floating point
        // inaccuracy
        const tolerance = 5;

        if (toolbarMiddleCanvasCoords.x < originalImageMiddle - tolerance) {
          // Keep the left edge in the same position
          const proposedNewLeft = image.left;
          const proposedNewMiddle = proposedNewLeft + (image.width * size) / 2;
          if (proposedNewMiddle < toolbarMiddleCanvasCoords.x) {
            newLeft = newLeftToAlignWithToolbarMiddle;
          } else {
            newLeft = proposedNewLeft;
          }
        } else if (
          toolbarMiddleCanvasCoords.x >
          originalImageMiddle + tolerance
        ) {
          // Keep the right edge in the same position
          const newRight = image.left + image.width * image.scaleX;
          const proposedNewLeft = newRight - image.width * size;
          const proposedNewMiddle = proposedNewLeft + (image.width * size) / 2;
          if (proposedNewMiddle > toolbarMiddleCanvasCoords.x) {
            newLeft = newLeftToAlignWithToolbarMiddle;
          } else {
            newLeft = proposedNewLeft;
          }
        }

        // Vertical considerations:
        // 1. The toolbar is above the image, so we want to keep the images top
        //    edge in the same position.
        // 2. The toolbar is below the image, so we want to keep the images
        //    bottom edge in the same position.
        // 3. The toolbar is in the middle of the image, so we want to keep the
        //    image in the same position relative to the toolbar.
        if (
          toolbarMiddleCanvasCoords.y >
          image.top + image.height * image.scaleY
        ) {
          // Move the image so its bottom edge stays in the same position
          const bottom = image.top + image.height * image.scaleY;
          newTop = bottom - image.height * size;
        } else if (toolbarMiddleCanvasCoords.y > image.top) {
          // Move the image vertically so that it stays in the same position
          // vertically relative to the toolbar (ratio of the image above to
          // below the toolbar)
          const percentAbove =
            (toolbarMiddleCanvasCoords.y - image.top) /
            (image.height * image.scaleY);
          newTop =
            toolbarMiddleCanvasCoords.y - image.height * size * percentAbove;
        } else {
          // Toolbar is above, keep the image at the same top position
          newTop = image.top;
        }
      } else {
        console.debug('Could not find the toolbar or canvas wrapper');
      }

      image.set('scaleX', size);
      image.set('scaleY', size);
      image.set('left', newLeft);
      image.set('top', newTop);

      hidePopup();
      Tool.toolActionComplete({ controller });

      // Request canvas re-render to update the display
      controller.canvas.requestRenderAll();
    };

    return (
      <ToolbarMenu columns={1} inheritWidth={true}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {Object.entries(SIZE_SCALE_VALUES).map(([label, size]) => (
            <ToolButton
              className={`${styles.toolButtonWide} ${styles.toolButtonConfigText}`}
              key={size}
              onClick={() => setSize(size)}
              name={label}
              // Using the first object of the selection as a proxy for all
              // of them is not ideal, but in practice it works well.
              active={selection.firstObject().scaleX === size}
            >
              {label}
            </ToolButton>
          ))}
        </div>
      </ToolbarMenu>
    );
  }
);

const SizeButton: ToolButtonComponent = ApplicationView(({ selection }) => {
  const tippyRef = useRef<TippyInstance>();
  const size =
    Object.keys(SIZE_SCALE_VALUES).find(
      key => SIZE_SCALE_VALUES[key] === selection.firstObject().scaleX
    ) ?? 'custom';
  const [currentSize, setCurrentSize] = useState(size);
  const placeholder = currentSize === 'custom' ? EM_DASH : size;

  return (
    <ToolButtonWithLabel
      popup={
        <Popup
          setCurrentSize={setCurrentSize}
          selection={selection}
          hidePopup={() => tippyRef.current?.hide()}
        />
      }
      tippyRef={tippyRef}
      label={'Size'}
    >
      {placeholder}
    </ToolButtonWithLabel>
  );
});

class ToolSize extends Tool {
  static toolName = 'Size';

  static buttonComponent() {
    return SizeButton;
  }
}

export default ToolSize;
